/**
 * Вкладка «Карточки МП» — то, что макет показывает покупателю.
 *
 * Основной сценарий — просмотр: у макета бывает два десятка карточек в
 * нескольких кабинетах, и первым экраном идёт их обзор. Подробности карточки
 * раскрываются в строке, а отдельный экран нужен, только когда смотрят все поля
 * площадки. Третий режим — сравнение WB и OZON рядом: тексты им генерируются
 * раздельно, и различие иначе не увидеть.
 *
 * Готовность больше не считается здесь: `readiness` приходит с бэкенда тем же,
 * каким его посчитали при заморозке черновика. Фронт лишь переводит коды причин.
 */

import React from 'react'
import { CheckCircle2, AlertTriangle, Store } from 'lucide-react'
import { EmptyState } from '../../../../components/ui/EmptyState'
import { Skeleton } from '../../../../components/ui/Skeleton'
import { PhotoResendModal } from '../../../../components/marketplace/PhotoResendModal'
import type { PhotoResendInput } from '../../../../components/marketplace/photoResendApi'
import type { MarketplaceCardDto, MarketplaceFieldLabels } from '../../../pipeline/api'
import { SafeImg } from './parts'
import { MarketplaceOverview, cardKey, type OverviewFilter, type OverviewGrouping } from './MarketplaceOverview'
import { MarketplaceCardDetail } from './MarketplaceCardDetail'
import { MarketplaceCompare } from './MarketplaceCompare'
import { cardState, hasWarning, isBlocked } from './marketplaceCardState'

export interface ProductVisuals {
  article: string
  image_urls: string[]
}

interface MarketplaceTabProps {
  cards: MarketplaceCardDto[] | null
  /** Визуалы товаров — единственное, что есть до появления карточек. */
  visuals: ProductVisuals[] | null
  pipelineStatus?: string | null
  /** Подписи полей из справочника характеристик площадки. */
  fieldLabels?: MarketplaceFieldLabels | null
  onOpenVisual: (urls: string[], index: number, article: string) => void
  /** Код макета — вход «Переотправить все фото макета» (задача B2). */
  designCode?: string
  /** Право `Card:UPDATE` — показывать чекбоксы и кнопки «Переотправить фото». */
  canResendPhotos?: boolean
  /** Фильтр площадки при открытии вкладки: клик по ВБ · ОЗ · ЯМ в цепочке окна. */
  initialFilter?: OverviewFilter
}

type View = 'list' | 'card' | 'compare'

const plural = (n: number, one: string, few: string, many: string) => {
  const mod100 = n % 100
  const mod10 = n % 10
  if (mod100 >= 11 && mod100 <= 14) return `${n} ${many}`
  if (mod10 === 1) return `${n} ${one}`
  if (mod10 >= 2 && mod10 <= 4) return `${n} ${few}`
  return `${n} ${many}`
}

export const MarketplaceTab: React.FC<MarketplaceTabProps> = ({
  cards,
  visuals,
  pipelineStatus,
  fieldLabels = null,
  onOpenVisual,
  designCode,
  canResendPhotos = false,
  initialFilter = 'all',
}) => {
  const [view, setView] = React.useState<View>('list')
  const [selected, setSelected] = React.useState<string | null>(null)
  const [expanded, setExpanded] = React.useState<string | null>(null)
  const [grouping, setGrouping] = React.useState<OverviewGrouping>('cab')
  const [filter, setFilter] = React.useState<OverviewFilter>(initialFilter)
  const [photoResendInput, setPhotoResendInput] = React.useState<PhotoResendInput | null>(null)
  const [photoResendOpen, setPhotoResendOpen] = React.useState(false)

  const openPhotoResend = (input: PhotoResendInput) => {
    setPhotoResendInput(input)
    setPhotoResendOpen(true)
  }

  const list = cards ?? []
  const current = selected ? list.find((card) => cardKey(card) === selected) ?? null : null

  // Карточка могла исчезнуть из выдачи, пока её смотрели, — возвращаемся к обзору,
  // а не показываем пустой экран.
  React.useEffect(() => {
    if (view !== 'list' && !current) setView('list')
  }, [view, current])

  if (cards === null) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton variant="custom" className="rounded-md" height={64} />
        <Skeleton variant="custom" className="rounded-md" height={180} />
      </div>
    )
  }

  // Карточек нет — показываем визуалы товаров, а не пустой экран.
  if (cards.length === 0) {
    const withVisuals = (visuals || []).filter((item) => item.image_urls?.length)
    if (withVisuals.length > 0) {
      return (
        <div className="space-y-4">
          <div className="rounded-lg border border-indigo-200 bg-indigo-50/70 p-3.5 text-[0.8125rem] text-gray-700 dark:border-indigo-900 dark:bg-indigo-950/30 dark:text-zinc-300">
            <b className="block text-sm font-semibold text-gray-900 dark:text-zinc-100">
              Карточек на маркетплейсах пока нет
            </b>
            Визуалы готовы. Карточки выгружаются на площадки из раздела «Черновики ПИМ» в каталогах ВБ,
            ОЗОН и Яндекс Маркета.
          </div>
          {withVisuals.map((item) => (
            <section
              key={item.article}
              className="overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-800"
            >
              <header className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-3.5 py-2 dark:border-zinc-800 dark:bg-zinc-900">
                <span className="font-mono text-[0.8125rem] font-semibold">{item.article}</span>
                <span className="ml-auto text-2xs text-gray-500 dark:text-zinc-400">
                  {plural(item.image_urls.length, 'визуал', 'визуала', 'визуалов')}
                </span>
              </header>
              <div className="flex flex-wrap gap-2 p-3">
                {item.image_urls.map((url, index) => (
                  <button
                    key={url}
                    type="button"
                    title="Открыть в полном размере"
                    onClick={() => onOpenVisual(item.image_urls, index, item.article)}
                    className="aspect-[3/4] w-[5.5rem] cursor-zoom-in overflow-hidden rounded border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-700"
                  >
                    <SafeImg
                      src={url}
                      alt={`${item.article} — визуал ${index + 1}`}
                      className="h-full w-full object-cover"
                      iconClass="h-4 w-4"
                    />
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )
    }

    return (
      <EmptyState
        title="Карточек на маркетплейсах нет"
        description={
          pipelineStatus === 'on_acceptance' || pipelineStatus === 'returned_to_designer'
            ? 'Макет ещё не принят. Черновики карточек появляются после приёмки и генерации визуалов.'
            : 'Визуалы ещё не сгенерированы. Когда движок отработает, они появятся здесь, а выкладка на площадки — из «Черновиков ПИМ».'
        }
      />
    )
  }

  const blocked = list.filter(isBlocked)
  const warned = list.filter((card) => !isBlocked(card) && hasWarning(card))
  const cabinets = new Set(list.map((card) => card.seller_name || `Кабинет ${card.seller_id}`)).size
  const articles = new Set(list.map((card) => card.article)).size
  const onStorefront = list.filter((card) => card.status === 'selling').length

  const summaryTone = blocked.length > 0 ? 'err' : warned.length > 0 ? 'warn' : 'ok'
  const summaryTitle =
    blocked.length > 0
      ? `${plural(blocked.length, 'карточка', 'карточки', 'карточек')} из ${list.length} ${
          blocked.length === 1 ? 'требует' : 'требуют'
        } внимания`
      : warned.length > 0
        ? `${list.length} карточек, замечания в ${warned.length}`
        : onStorefront === list.length
          ? `${plural(list.length, 'карточка', 'карточки', 'карточек')} на витринах`
          : `Все ${list.length} карточек готовы к публикации`

  const openCard = (key: string) => {
    setSelected(key)
    setView('card')
  }
  const openCompare = (key: string) => {
    setSelected(key)
    setView('compare')
  }

  return (
    <div className="space-y-3">
      <div
        className={`flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2.5 ${
          summaryTone === 'err'
            ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30'
            : summaryTone === 'warn'
              ? 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30'
              : 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/20'
        }`}
      >
        {summaryTone === 'ok' ? (
          onStorefront === list.length ? (
            <Store className="h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
          ) : (
            <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
          )
        ) : (
          <AlertTriangle
            className={`h-5 w-5 shrink-0 ${
              summaryTone === 'err'
                ? 'text-red-600 dark:text-red-400'
                : 'text-amber-600 dark:text-amber-400'
            }`}
          />
        )}
        <div>
          <strong className="block text-sm">{summaryTitle}</strong>
          <span className="text-xs text-gray-600 dark:text-zinc-400">
            {plural(articles, 'артикул', 'артикула', 'артикулов')} ·{' '}
            {plural(cabinets, 'кабинет', 'кабинета', 'кабинетов')}
          </span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {blocked.length > 0 && view === 'list' && filter !== 'problem' && (
            <button
              type="button"
              onClick={() => setFilter('problem')}
              className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600 hover:border-gray-300 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
            >
              Показать только проблемные
            </button>
          )}
        </div>
      </div>

      {/*
        Коммерческие значения сегодня никуда не уезжают: цена и остаток живут
        только в PIM. Предупреждение висит на всей вкладке, а не прячется в
        раскрытой строке — иначе «Цена только в PIM» читается как «цена на
        площадке», чего никто не подтверждал.
      */}
      {list.some((card) => cardState(card) === 'commercial_pim') && (
        <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          Цена и плановый остаток сохранены только в PIM. Внешнее состояние карточки на маркетплейсе
          не подтверждено, отправка коммерческих значений не подключена.
        </p>
      )}

      {view === 'list' && (
        <MarketplaceOverview
          cards={list}
          fieldLabels={fieldLabels}
          grouping={grouping}
          filter={filter}
          expandedKey={expanded}
          onGrouping={setGrouping}
          onFilter={setFilter}
          onToggle={(key) => setExpanded(expanded === key ? null : key)}
          onOpenCard={openCard}
          onCompare={openCompare}
          onOpenVisual={onOpenVisual}
          designCode={designCode}
          canResendPhotos={canResendPhotos}
          onResendPhotos={openPhotoResend}
        />
      )}

      {view === 'card' && current && (
        <MarketplaceCardDetail
          card={current}
          twin={
            list.find(
              (card) => card.article === current.article && card.marketplace !== current.marketplace,
            ) ?? null
          }
          fieldLabels={fieldLabels}
          onOpenVisual={onOpenVisual}
          onBack={() => setView('list')}
          onCompare={() => setView('compare')}
        />
      )}

      {view === 'compare' && current && (
        <MarketplaceCompare
          cards={list}
          article={current.article}
          onBack={() => setView('list')}
          onOpenVisual={onOpenVisual}
        />
      )}

      <PhotoResendModal
        isOpen={photoResendOpen}
        onClose={() => setPhotoResendOpen(false)}
        input={photoResendInput}
      />
    </div>
  )
}

export default MarketplaceTab
